// utils/compliance.js
// ---------------------------------------------------------------------------
// PURE compliance engine for VAUOM Design Analysis. No React, no THREE.
//
// Verifies the ACHIEVED design (massing / parsed plans / inputs) against the
// RESOLVED governing DCR values, in three categories: exterior, interior,
// amenities. Screening-grade — briefs the architect + a formal sanction
// review; not a sanctioned calc.
//
// Two operators are kept deliberately separate:
//   resolve : picks WHICH required value governs across interacting rules
//             (e.g. GDCR front margin = max(road-width rule 6m, height rule 8m)
//              -> 8m governs). This is the Vapi case.
//   check   : compares the achieved value against that governing value
//             (min -> achieved must be >=, max -> achieved must be <=).
//
// The param-file shape this consumes is documented in the sample JSON.
// ---------------------------------------------------------------------------

const EPS = 1e-6;

// severity rank, worst first — used to summarise a category/verdict set
const SEVERITY_RANK = { demolition: 0, "stop-work": 1, "oc-denial": 2, compounding: 3 };

/**
 * Evaluate a `when` predicate object against project facts.
 * Keys map a fact name to either a scalar (eq) or an operator object
 * { gte, gt, lte, lt, eq, ne, in }. Multiple keys are ANDed. Empty = always.
 */
export function matchPredicate(when, facts) {
  if (!when || typeof when !== "object") return true;
  return Object.entries(when).every(([key, cond]) => {
    const v = facts ? facts[key] : undefined;
    if (cond === null || typeof cond !== "object") return v === cond; // scalar eq
    if ("eq" in cond && !(v === cond.eq)) return false;
    if ("ne" in cond && !(v !== cond.ne)) return false;
    if ("in" in cond && !(Array.isArray(cond.in) && cond.in.includes(v))) return false;
    if (v == null) {
      // numeric comparisons against a missing fact can't be satisfied
      if ("gte" in cond || "gt" in cond || "lte" in cond || "lt" in cond) return false;
    }
    if ("gte" in cond && !(v >= cond.gte)) return false;
    if ("gt" in cond && !(v > cond.gt)) return false;
    if ("lte" in cond && !(v <= cond.lte)) return false;
    if ("lt" in cond && !(v < cond.lt)) return false;
    return true;
  });
}

/**
 * Resolve the governing required value for a parameter given project facts.
 * Returns { value, governingClauseId, candidates, reason }.
 *   candidates = every rule that matched (so the UI can show "6m vs 8m -> 8m").
 *   value = null when no rule matched (parameter not covered for these facts).
 */
export function resolveRequired(param, facts) {
  // Already resolved upstream (by dcrResolve.js across the precedence stack).
  if (param._resolved && param._resolved.value != null) {
    return {
      value: param._resolved.value,
      governingClauseId: param.governingClauseId || null,
      candidates: param.candidates || [],
      reason: "pre-resolved",
    };
  }
  const matching = (param.rules || []).filter((r) => matchPredicate(r.when, facts));
  const candidates = matching.map((r) => ({ value: r.value, clauseId: r.clauseId || null }));
  if (candidates.length === 0) {
    return { value: null, governingClauseId: null, candidates: [], reason: "no-rule-matched" };
  }
  let governing = candidates[0];
  const mode = param.resolve || "max";
  if (mode === "max") {
    governing = candidates.reduce((a, b) => (b.value > a.value ? b : a));
  } else if (mode === "min") {
    governing = candidates.reduce((a, b) => (b.value < a.value ? b : a));
  } // "first" -> keep candidates[0]
  return {
    value: governing.value,
    governingClauseId: governing.clauseId,
    candidates,
    reason: "resolved",
  };
}

/** Is this (amenity) parameter required at all, given facts? Default: yes. */
export function isRequired(param, facts) {
  if (!param.requiredWhen) return true;
  return matchPredicate(param.requiredWhen, facts);
}

/**
 * Produce a verdict for one parameter.
 * @param param    the param definition from the DCR file
 * @param achieved { value, source: 'massing'|'plan'|'input', confidence } | undefined
 * @param facts    project classifier facts
 * @param opts     { id, category }
 */
export function checkParameter(param, achieved, facts, opts = {}) {
  const base = {
    id: opts.id || param.id || null,
    category: opts.category || null,
    label: param.label || opts.id || "",
    unit: param.unit || "",
    check: param.check || "min",
    severity: param.severity || "compounding",
    confidence: param.confidence || null,
    required: { value: null, governingClauseId: null, candidates: [], source: param.source || "dcr" },
    achieved: achieved || { value: null, source: null, confidence: null },
    status: "not-encoded",
    message: "",
  };

  // 1) applicability gate (amenities)
  if (!isRequired(param, facts)) {
    return { ...base, status: "not-applicable", message: "Not required for this project." };
  }

  // 2) resolve governing requirement
  const req = resolveRequired(param, facts);
  base.required = { ...req, source: param.source || "dcr" };
  if (req.value == null) {
    return { ...base, status: "not-encoded", message: "No rule covers this jurisdiction/case yet." };
  }

  // 3) do we have an achieved value to compare?
  if (achieved == null || achieved.value == null) {
    const need =
      (param.achievedSource === "plan" && "needs-parse") ||
      (param.achievedSource === "input" && "needs-input") ||
      "needs-input";
    return { ...base, status: need, message: needMessage(need) };
  }

  // 4) compare by check operator
  const a = round(achieved.value);
  const r = round(req.value);
  let pass;
  switch (param.check) {
    case "max": pass = a <= r + EPS; break;            // caps: coverage, FSI, height
    case "min": pass = a >= r - EPS; break;            // gaps: setbacks, room size, window %
    case "required-present": pass = !!achieved.value; break; // amenity presence
    default: pass = a >= r - EPS;
  }

  return {
    ...base,
    status: pass ? "pass" : "fail",
    message: pass
      ? ""
      : failMessage(param, a, r, base.unit),
  };
}

/**
 * Run the full check set over the three categories.
 * @param paramFile resolved DCR file { jurisdiction, version, regulationName, parameters:{exterior,interior,amenities} }
 * @param achieved  { exterior:{id:{value,source,confidence}}, interior:{...}, amenities:{...} }
 * @param facts     classifier facts { road_width_m, building_height_m, plot_area_m2, use, floors, zone, ... }
 * @param meta      { zoneConfidence } — propagated into the summary
 */
export function runCompliance(paramFile, achieved = {}, facts = {}, meta = {}) {
  const categories = ["exterior", "interior", "amenities"];
  const params = (paramFile && paramFile.parameters) || {};
  const out = { exterior: [], interior: [], amenities: [] };

  categories.forEach((cat) => {
    const defs = params[cat] || {};
    const achievedCat = achieved[cat] || {};
    out[cat] = Object.entries(defs).map(([id, def]) =>
      checkParameter(def, achievedCat[id], facts, { id, category: cat })
    );
  });

  return {
    jurisdiction: paramFile && paramFile.jurisdiction,
    version: paramFile && paramFile.version,
    regulationName: paramFile && paramFile.regulationName,
    zoneConfidence: meta.zoneConfidence || null,
    categories: out,
    summary: summarise(out),
  };
}

// ---- summaries & messages -------------------------------------------------

function summarise(out) {
  const all = [...out.exterior, ...out.interior, ...out.amenities];
  const by = (s) => all.filter((v) => v.status === s).length;
  const fails = all.filter((v) => v.status === "fail");
  const worst = fails.reduce(
    (w, v) => (SEVERITY_RANK[v.severity] < SEVERITY_RANK[w] ? v.severity : w),
    "compounding"
  );
  return {
    total: all.length,
    pass: by("pass"),
    fail: fails.length,
    notEncoded: by("not-encoded"),
    notApplicable: by("not-applicable"),
    needsInput: by("needs-input") + by("needs-parse"),
    worstFailSeverity: fails.length ? worst : null,
  };
}

function needMessage(need) {
  if (need === "needs-parse") return "Upload & parse a floor plan to verify this.";
  return "Enter the value to verify this.";
}
function failMessage(param, a, r, unit) {
  const dir = param.check === "max" ? "exceeds max" : "below min";
  return `Achieved ${a}${unit} ${dir} ${r}${unit}.`;
}
function round(n, dp = 4) {
  const f = Math.pow(10, dp);
  return Math.round((Number(n) + Number.EPSILON) * f) / f;
}

export const SEVERITY_ORDER = SEVERITY_RANK;
